import { createHashRouter, type RouteObject } from 'react-router';
// The ROUTING TABLE, not the manifest (T-321 / BUG-155): the router needs each route's id, path and `shell`
// flag, and importing the manifest for them put all 2,300 lines of QA declaration in the app's first chunk
// (REQ-280 AC1 / AC3). The manifest is BUILT from this table, so "the router is built from the list of
// routes" is unchanged — see the header of `routes.table.ts`.
import { routesTable, type RouteTableEntry } from '../routes.table';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { AppShell, ShellFallback } from './shell/AppShell';
import { WelcomeRoute } from '../routes/welcome/WelcomePage';

// Hash routing for every MVP build (ARCH §9.2): snapshots are static files in a sub-folder with no SPA
// fallback, so `#/home` survives a reload where `/home` would 404. Switching to browser history later is a
// one-line change over the same route objects.
//
// Routes come FROM the routing table: each entry's id maps to a module here, and a test fails when the two lists
// drift in either direction. Every route gets its own error boundary (ARCH §9.1).

type LazyRoute = NonNullable<RouteObject['lazy']>;

/**
 * BUG-054 · **every lazy route goes through here, so a missing chunk always becomes a THROWN error.**
 *
 * Rehearsing the founder's case (the preview folder swapped under an open tab) showed the failure is not
 * always a rejected import. A static host with an SPA fallback answers a missing `assets/route-*.js` with
 * `index.html`, the browser refuses it ("Expected a JavaScript-or-Wasm module script … MIME type of
 * text/html") and React Router ends up matching a route with **no Component at all** — it warns
 * "Matched leaf route … does not have an element or Component … resulting in an empty page" and renders
 * NOTHING. No boundary, no sentence: a blank page inside a working shell, which is the worst of the three
 * possible outcomes and exactly what "mostly non functional" looked like.
 *
 * So the wrapper asserts what the router is about to rely on — the module arrived and it has a Component —
 * and turns anything else into the `TypeError` the boundary recognises (`isStaleBuildError`). The guard in
 * `app/staleBuild.ts` still reloads once before this is ever seen; this is what the reader gets when the
 * reload did not help.
 */
type LazyModule = { Component?: unknown; element?: unknown };

function chunk(load: () => Promise<unknown>): LazyRoute {
  return (async () => {
    const module = (await load()) as LazyModule | null;
    if (module === null || (module.Component === undefined && module.element === undefined)) {
      throw new TypeError(
        'Failed to fetch dynamically imported module: the route file did not arrive (stale build)',
      );
    }
    return module;
  }) as LazyRoute;
}

// The welcome route is the landing page: eager, so the first paint needs no second round trip. Everything
// else is code-split (ARCH §9.1) — the Home chunk, and through it the demo server, load on "Try the demo".
const eagerRoutes: Record<string, RouteObject['Component']> = {
  welcome: WelcomeRoute,
};
const lazyRoutes: Record<string, LazyRoute> = {
  signup: chunk(() => import('../routes/acquisition/AuthPage')),
  login: chunk(() => import('../routes/acquisition/AuthPage')),
  account: chunk(() => import('../routes/acquisition/routeAccount')),
  terms: chunk(() => import('../routes/acquisition/LegalPage')),
  privacy: chunk(() => import('../routes/acquisition/LegalPage')),
  home: chunk(() => import('../routes/home/route')),
  campaigns: chunk(() => import('../routes/campaigns/route')),
  // The entity detail (T-046): its own chunk, reached from a row's name, an alert or a deep link.
  'entity/:id': chunk(() => import('../routes/entity/route')),
  // Spend & Budgets (T-091 / T-092): two sub-tabs, two addresses, one frame. Their own chunk, because the
  // pacing bar behind it reaches `@sl/core/charts` and that is where d3 lives (ARCH §8.1).
  'spend/pacing': chunk(() => import('../routes/spend/route')),
  'spend/allocation': chunk(() => import('../routes/spend/routeAllocation')),
  // The creatives gallery (T-097): its own chunk — a wall of pictures nobody on Home needs to download.
  creatives: chunk(() => import('../routes/creatives/route')),
  // The alerts inbox (T-170): its own chunk — two views of one paginated list, plus the sparkline renderer
  // behind it, which nobody on Home needs to download. It was the last "arrives in" destination on the web.
  alerts: chunk(() => import('../routes/alerts/route')),
  // One alert (T-171 fills the frame in), and the rules list (T-174) — both under `/alerts`, both reached
  // from the inbox and from an `AppLink`, so neither is ever a dead link.
  'alerts/:alertId': chunk(() => import('../routes/alerts/routeDetail')),
  'alerts/rules': chunk(() => import('../routes/alerts/routeRules')),
  // The activity log (T-136): its own chunk — the account's whole history, paginated, which nobody on Home
  // needs to download. It was the last "arrives in" page on the web.
  activity: chunk(() => import('../routes/activity/route')),
  // Settings v1 (T-048): a real screen, so it has its own chunk rather than the placeholder's.
  settings: chunk(() => import('../routes/settings/route')),
};

export function routeObjectFor(entry: RouteTableEntry): RouteObject {
  const base = { id: entry.id, path: entry.path, ErrorBoundary: RouteErrorBoundary };
  const eager = eagerRoutes[entry.id];
  if (eager) return { ...base, Component: eager };
  const lazy = lazyRoutes[entry.id];
  if (lazy) return { ...base, lazy, ...(!entry.shell ? { HydrateFallback: PublicFallback } : {}) };
  throw new Error(
    `Route "${entry.id}" is in routes.table.ts but has no module in app/router.tsx (or the reverse).`,
  );
}

function PublicFallback() {
  return (
    <main aria-busy="true">
      <p role="status">Loading…</p>
    </main>
  );
}

/** The id of the layout route that frames every `shell: true` entry; it has no path of its own. */
export const SHELL_ROUTE_ID = 'shell';

export function buildRoutes(): RouteObject[] {
  const known = new Set([...Object.keys(eagerRoutes), ...Object.keys(lazyRoutes)]);
  for (const entry of routesTable) known.delete(entry.id);
  if (known.size) {
    throw new Error(
      `app/router.tsx registers routes missing from routes.table.ts: ${[...known].join(', ')}`,
    );
  }
  // The shell (T-036) is a pathless layout route: every route the manifest marks `shell: true` becomes its
  // child, so the nav, the mode chip and the freshness label are mounted ONCE and survive navigation between
  // them. Welcome (and later the first-run flow) stays outside it — no chip on the welcome screen
  // (REQ-006 AC5). Each screen keeps its own error boundary inside the shell, so a screen that throws leaves
  // the chrome standing; the shell's own failures are handled by `AppShell` itself.
  const framed = routesTable.filter((entry) => entry.shell).map(routeObjectFor);
  const bare = routesTable.filter((entry) => !entry.shell).map(routeObjectFor);
  return [
    ...bare,
    ...(framed.length
      ? [
          {
            id: SHELL_ROUTE_ID,
            Component: AppShell,
            // A screen that throws is caught by its OWN boundary and replaced inside the shell, so the nav
            // and the chip stay up; this one catches a throw in the chrome itself.
            ErrorBoundary: RouteErrorBoundary,
            // What the data router shows while the first lazy child chunk is still arriving.
            HydrateFallback: ShellFallback,
            // BUG-134 / D-499: an unknown address is a page INSIDE the shell (`not-found.*`), not a route error
            // outside it with a "Try again" that could not help.
            children: [
              ...framed,
              {
                path: '*',
                lazy: chunk(() => import('../routes/notFound/route')),
                ErrorBoundary: RouteErrorBoundary,
              },
            ],
          } satisfies RouteObject,
        ]
      : []),
  ];
}

/** Every leaf route of `buildRoutes()`, shell children included — what the manifest test compares against. */
export function flattenRoutes(routes: readonly RouteObject[]): RouteObject[] {
  return routes.flatMap((route) => (route.children ? flattenRoutes(route.children) : [route]));
}

export function createAppRouter() {
  return createHashRouter(buildRoutes());
}
